/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  BuilderFormSchemaFactory,
  BuilderUserContext, FormRootSchema,
  FormState, FormStateSchema, whenAuOrNzRegion,
  whenCreateOrEditMode,
} from '@gh/builder-shared';
import { BuilderPermission, SalesFolderStatus } from '@gh/builder-shared-data';
import {
  DateTimeTypes,
  isRouteForCreationMode,
  MaskedTypes,
  whenUsOrAuRegion,
  whenUsRegion,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import {
  FormMode,
  getControlValue,
  markControlAsDirty,
  setControlValue,
  latitudeValidator,
  longitudeValidator,
} from '@gh/core-ui';
import { currentDate } from '@gh/core-util';
import { RfqGeneral } from '../../resources/lead.model';
import { inspectionArr, RfqSite } from '../../resources/rfq.model';
import { ValidatorFn } from '@angular/forms';

@Injectable()
export class RfqSiteDetailsFormState extends FormState<RfqSite> {
  private generalInfo: RfqGeneral;

  constructor(schemaFactory: BuilderFormSchemaFactory,
              private userContext: BuilderUserContext,
              private route: ActivatedRoute) {
    super(schemaFactory);
  }

  build(siteDetail: RfqSite, generalInfo: RfqGeneral): void {
    this.generalInfo = generalInfo;

    this.initSchema(siteDetail);
  }

  updateInspectedDate(isInspected: boolean): void {
    const inspectedDateControl = this.get('inspectedDate');
    const dateValue = isInspected ? currentDate() : void 0;

    inspectedDateControl.setValue(dateValue);
    inspectedDateControl.updateSchema({
      required: isInspected,
      enabled: isInspected,
    });
  }

  googleMapUrlValidator(): ValidatorFn {
    return (control): { [key: string]: any } | null => {
      const value = control.value;
      const maxLength = 500;
      if (!value) {
        return null;
      }
      if (value.length > maxLength) {
        return { pattern: { messageId: 'msg_field_maxlength', requiredLength: maxLength } };
      }
      const patternGoogleUrl = /^https:\/\/www\.google\.com(.[a-z]{2})?\/maps\//;
      const invalid = !patternGoogleUrl.test(value);

      return invalid ? { pattern: { messageId: 'msg_invalid_url' } } : null;
    };
  }

  setInspectionValue(inspectionId: ID, value: any): void {
    setControlValue(
      this.group,
      'inspections',
      inspectionArr.merge(getControlValue(this.group, 'inspections'), inspectionId, { value }));
    markControlAsDirty(this.group, 'inspections');
  }

  protected buildSchema(siteDetail: RfqSite, { root }: FormStateSchema): FormRootSchema {
    const mode = isRouteForCreationMode(this.route) ? FormMode.CreateMode :
      this.userContext.hasPermission(BuilderPermission.Sales.Leads.EDIT)
      && this.generalInfo.status === SalesFolderStatus.Active ? FormMode.EditMode : FormMode.ViewMode;

    return root({
      enabled: whenCreateOrEditMode(),
      mode,
      children: {
        id: void 0,
        name: { required: true },
        lotNumber: void 0,
        suburb: { required: whenAuOrNzRegion() },
        streetNumber: void 0,
        street: { required: whenAuOrNzRegion() },
        postCode: { visible: whenUsOrAuRegion(), type: MaskedTypes.Zip },
        latitude: { validators: [latitudeValidator()] },
        longitude: { validators: [longitudeValidator()] },
        reference: { visible: whenUsRegion() },
        inspectedDate: { required: true, enabled: siteDetail.isInspected, type: DateTimeTypes.ShortDate },
        isInspected: void 0,
        inspections: void 0,
      },
    });
  }
}
